import { useState, useEffect } from 'react';
import type { FormEvent } from 'react';
import firebase from 'firebase/compat/app';
import * as XLSX from 'xlsx';
import { surveyQuestions } from '../constants/survey';
import { auth, rootRef } from '../lib/firebase';
import { makePasswordFields, verifyPassword } from '../lib/password';
import { seedInitialData } from '../lib/seed';
import { migrateLegacyPeerEvals, isValidPeerAnswer } from '../lib/migratePeerEvals';
import { runPeerEvalAudit, formatAuditReport } from '../lib/auditPeerEvals';
import {
    authEmailFor,
    bindMemberSession,
    clearAuthLink,
    createAuthUserKeepingSession,
    describeAuthError,
    isAuthProviderError,
    shouldSuppressAnonymousRestore,
    writeAuthLink,
} from '../lib/authAccount';
import {
    buildSessionUser,
    readStoredMemberId,
    writeStoredMemberId,
    clearStoredSession,
    sessionLooksStale,
    hasRole,
} from '../lib/sessionAuth';
import {
    emptyCategoryCounts,
    upsertPeerAnswerToReport,
    upsertPeerCommentsToReport,
    backfillPeerReportSummaries,
} from '../lib/peerReportSummary';
import { suggestBehaviorFromPeer } from '../lib/peerCalibrationLink';
import { buildHrMonitorSnapshot } from '../lib/hrMonitoring';
import type { HrMonitorSnapshot } from '../lib/hrMonitoring';
import { isPeerEvalSubmitted, submitPeerEvaluation } from '../lib/peerSubmission';
import { deleteMemberCascade } from '../lib/deleteMemberCascade';
import { isMemberDisabled, setMemberDisabled } from '../lib/memberLifecycle';
import { writeAuditLog } from '../lib/auditLog';
import {
    emptyYearSeasonLock,
    isQuarterSeasonLocked,
    normalizeSeasonLock,
    seasonLockDocId,
    type YearSeasonLock,
} from '../lib/seasonLock';
import {
    evalGroupLabel,
    recommendedPoolIds,
    formatCoworkerNames,
} from '../lib/taskProcess';
import {
    availableEvalYears,
    defaultEvalYear,
    readStoredEvalYear,
    writeStoredEvalYear,
    matchesEvalYear,
    peerEvalDocId,
    peerCommentDocId,
    peerSummaryDocId,
    poolDocId,
} from '../lib/evalYear';
import { gradesForYear } from '../lib/memberGrades';
import { getBlockTitle, calculateQuantitativeRating, calculateQualitativeRating } from '../lib/gradeRules';
import { useToast } from './useToast';
import type {
    ActiveTab,
    EvaluatorPool,
    Member,
    OrgTeam,
    PeerAnswer,
    PeerComment,
    PeerReportSummary,
    SessionUser,
    TaskRecord,
    UserRole,
} from '../types';

export interface NewTaskPayload {
    title: string;
    type: string;
    taskCategory: string;
    targetValue: string;
    period: string;
    coworkerIds: string[];
}

type CurrentPool = EvaluatorPool | { selectedIds: string[]; confirmed: boolean };

export function useAppStore() {
    // Firebase Auth 로그인 상태
    const [firebaseUser, setFirebaseUser] = useState<firebase.User | null>(null);

    // 로그인한 개인 세션 (권한은 DB members 기준으로만 결정)
    const [sessionUser, setSessionUser] = useState<SessionUser | null>(null);
    // 새로고침 직후: storage의 memberId. members 스냅샷이 오면 DB에서 세션 재조립
    const [pendingMemberId, setPendingMemberId] = useState<string | null>(() => readStoredMemberId());
    const [membersLoaded, setMembersLoaded] = useState(false);
    const [evalYear, setEvalYear] = useState<number>(() => readStoredEvalYear());
    const evalYearOptions = availableEvalYears();
    const legacyYearFallback = defaultEvalYear();

    // 실시간 DB 동기화 상태
    const [tasks, setTasks] = useState<TaskRecord[]>([]);
    const [departmentMembers, setDepartmentMembers] = useState<Member[]>([]);
    const [departments, setDepartments] = useState<string[]>([]);
    const [orgTeamList, setOrgTeamList] = useState<OrgTeam[]>([]);
    const [peerAnswers, setPeerAnswers] = useState<PeerAnswer[]>([]);       // 본인 작성 응답 (HR은 전체)
    const [peerComments, setPeerComments] = useState<PeerComment[]>([]);     // 본인 작성 코멘트 (HR은 전체)
    const [peerReportSummary, setPeerReportSummary] = useState<PeerReportSummary | null>(null); // 익명 집계 리포트 (본인)
    const [peerReportByMember, setPeerReportByMember] = useState<Record<string, PeerReportSummary>>({}); // Calibration용 연도별 익명 집계
    const [evaluatorPools, setEvaluatorPools] = useState<Record<string, EvaluatorPool>>({}); // { subjectId: { selectedIds, confirmed, ... } }
    const [seasonLock, setSeasonLock] = useState<YearSeasonLock>(() => emptyYearSeasonLock(readStoredEvalYear()));
    const [publicRegistrationOpen, setPublicRegistrationOpen] = useState(true);

    const [activeTab, setActiveTab] = useState<ActiveTab>('tasks');

    const [isRegisterOpen, setIsRegisterOpen] = useState(false);

    // 다면평가
    const [activeColleagueToEvaluate, setActiveColleagueToEvaluate] = useState<Member | null>(null); // member 객체
    const [commentStrength, setCommentStrength] = useState('');
    const [commentImprovement, setCommentImprovement] = useState('');
    const [selectedPoolSubject, setSelectedPoolSubject] = useState('');

    // Calibration
    const [selectedMemberForCalibration, setSelectedMemberForCalibration] = useState<Member | null>(null);
    const [editPerfGrade, setEditPerfGrade] = useState('GD');
    const [editBehaviorGrade, setEditBehaviorGrade] = useState('GD');
    const [editNote, setEditNote] = useState('');
    const [selectedDeptFilter, setSelectedDeptFilter] = useState('ALL');

    // HR 어드민
    const [newDeptOrg, setNewDeptOrg] = useState('');
    const [newDeptTeam, setNewDeptTeam] = useState('');
    const [newMemberName, setNewMemberName] = useState('');
    const [newMemberDuty, setNewMemberDuty] = useState('팀원');
    const [newMemberDept, setNewMemberDept] = useState('');
    const [newMemberOrg, setNewMemberOrg] = useState('');
    const [newMemberTeam, setNewMemberTeam] = useState('');
    const [adminMemberOrg, setAdminMemberOrg] = useState('');
    const [adminMemberTeam, setAdminMemberTeam] = useState('');
    const [adminMemberDept, setAdminMemberDept] = useState('');
    const [newMemberPerf, setNewMemberPerf] = useState('GD');
    const [newMemberBehav, setNewMemberBehav] = useState('GD');
    const [newMemberNote, setNewMemberNote] = useState('');
    const [newMemberEvalGroup, setNewMemberEvalGroup] = useState('');
    const [adminInitPassword, setAdminInitPassword] = useState('');

    // 인증 관련
    const [newMemberPassword, setNewMemberPassword] = useState('');
    const [newMemberPasswordConfirm, setNewMemberPasswordConfirm] = useState('');
    const [loginName, setLoginName] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const [isAuthBusy, setIsAuthBusy] = useState(false);

    // 비밀번호 변경 모달
    const [isPwModalOpen, setIsPwModalOpen] = useState(false);
    const [pwCurrent, setPwCurrent] = useState('');
    const [pwNew, setPwNew] = useState('');
    const [pwNewConfirm, setPwNewConfirm] = useState('');
    const [pwError, setPwError] = useState('');

    // 토스트 (타이머 중첩 방지)
    const { toastMessage, showToast, dbError } = useToast();

    // 1. Firebase 인증: 이미 Email 세션이 있으면 유지, 없으면 익명(로그인 화면용)
    useEffect(() => {
        let cancelled = false;
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (cancelled) return;
            if (user) {
                setFirebaseUser(user);
                try {
                    await seedInitialData();
                    await migrateLegacyPeerEvals();
                } catch (err) {
                    console.error("Seed/Migrate Error:", err);
                }
                return;
            }
            setFirebaseUser(null);
            if (shouldSuppressAnonymousRestore()) {
                return;
            }
            try {
                await auth.signInAnonymously();
            } catch (e) {
                console.error("Firebase Auth Error:", e);
                showToast("서버 인증 오류입니다. Firebase 콘솔에서 '익명 로그인'이 활성화되어 있는지 확인해주세요.");
            }
        });
        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, []);

    // 개발/운영 점검용: window window.__auditPeer() / window.__backfillPeerReports()
    useEffect(() => {
        window.__auditPeer = runPeerEvalAudit;
        window.__backfillPeerReports = backfillPeerReportSummaries;
        return () => {
            delete window.__auditPeer;
            delete window.__backfillPeerReports;
        };
    }, []);

    // 2. 인증 완료 후 Firestore 실시간 동기화
    useEffect(() => {
        if (!firebaseUser) return;

        const unsubDepts = rootRef().collection('departments').onSnapshot((snapshot) => {
            const deptsList: string[] = [];
            const orgTeamPairs: OrgTeam[] = [];
            snapshot.docs.forEach(doc => {
                const data = doc.data();
                deptsList.push(data.name);
                if (data.org && data.team) orgTeamPairs.push({ org: data.org, team: data.team });
            });
            deptsList.sort();
            orgTeamPairs.sort((a, b) => (a.org + a.team).localeCompare(b.org + b.team));
            setDepartments(deptsList);
            setOrgTeamList(orgTeamPairs);
        }, dbError("부서 목록 동기화"));

        const unsubMembers = rootRef().collection('members').onSnapshot((snapshot) => {
            setDepartmentMembers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Member[]);
            setMembersLoaded(true);
        }, dbError("임직원 마스터 동기화"));

        const unsubTasks = rootRef().collection('tasks').onSnapshot((snapshot) => {
            const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as TaskRecord[];
            list.sort((a, b) => (Number(b.createdAt) || 0) - (Number(a.createdAt) || 0));
            setTasks(list);
        }, dbError("중점 과제 동기화"));

        return () => {
            unsubDepts();
            unsubMembers();
            unsubTasks();
        };
    }, [firebaseUser]);

    // 2b. 선택 연도 시즌 잠금 설정
    useEffect(() => {
        if (!firebaseUser) return;
        const unsub = rootRef()
            .collection('settings')
            .doc(seasonLockDocId(evalYear))
            .onSnapshot((snap) => {
                setSeasonLock(normalizeSeasonLock(evalYear, snap.exists ? (snap.data() as YearSeasonLock) : null));
            }, dbError("시즌 잠금 동기화"));
        return () => unsub();
    }, [firebaseUser, evalYear]);

    // 2c. 공개 가입 허용 여부 (기본 true — HR가 끄면 신규 자가가입 불가)
    useEffect(() => {
        if (!firebaseUser) return;
        const unsub = rootRef()
            .collection('settings')
            .doc('registration')
            .onSnapshot((snap) => {
                const open = snap.exists ? snap.data()?.publicOpen !== false : true;
                setPublicRegistrationOpen(Boolean(open));
            }, dbError("가입 설정 동기화"));
        return () => unsub();
    }, [firebaseUser]);

    // 3. 로그인 세션 이후: 다면평가·풀은 역할별 최소 권한 + 평가연도 스코프
    useEffect(() => {
        if (!firebaseUser || !sessionUser) {
            setPeerAnswers([]);
            setPeerComments([]);
            setPeerReportSummary(null);
            setPeerReportByMember({});
            setEvaluatorPools({});
            return;
        }

        const isHr = sessionUser.role === 'HR';
        const isLeader = sessionUser.role === 'EVALUATOR' || isHr;

        const answersQuery = isHr
            ? rootRef().collection('peer_evals')
            : rootRef().collection('peer_evals').where('evaluatorId', '==', sessionUser.id);
        const unsubAnswers = answersQuery.onSnapshot((snapshot) => {
            const rows = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(a => matchesEvalYear((a as PeerAnswer).evalYear, evalYear, legacyYearFallback));
            setPeerAnswers(rows as PeerAnswer[]);
        }, dbError("다면평가 응답 동기화"));

        const commentsQuery = isHr
            ? rootRef().collection('peer_comments')
            : rootRef().collection('peer_comments').where('evaluatorId', '==', sessionUser.id);
        const unsubComments = commentsQuery.onSnapshot((snapshot) => {
            const rows = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(c => matchesEvalYear((c as PeerComment).evalYear, evalYear, legacyYearFallback));
            setPeerComments(rows as PeerComment[]);
        }, dbError("다면평가 코멘트 동기화"));

        let unsubSummary: () => void;
        if (isLeader) {
            unsubSummary = rootRef().collection('peer_report_summaries').onSnapshot((snap) => {
                const map: Record<string, PeerReportSummary> = {};
                snap.docs.forEach((doc) => {
                    const data = doc.data();
                    if (!matchesEvalYear(data.evalYear, evalYear, legacyYearFallback)) return;
                    const colleagueId = data.colleagueId || String(doc.id).replace(/^\d{4}_/, '');
                    map[colleagueId] = { id: doc.id, ...data } as PeerReportSummary;
                });
                setPeerReportByMember(map);
                setPeerReportSummary(map[sessionUser.id] || null);
            }, dbError("다면평가 익명 리포트 동기화"));
        } else {
            unsubSummary = rootRef().collection('peer_report_summaries')
                .doc(peerSummaryDocId(evalYear, sessionUser.id))
                .onSnapshot((snap) => {
                    const row = snap.exists ? ({ id: snap.id, ...snap.data() } as PeerReportSummary) : null;
                    setPeerReportSummary(row);
                    setPeerReportByMember(row ? { [sessionUser.id]: row } : {});
                }, dbError("다면평가 익명 리포트 동기화"));
        }

        const poolsQuery = isLeader
            ? rootRef().collection('evaluator_pools')
            : rootRef().collection('evaluator_pools').where('selectedIds', 'array-contains', sessionUser.id);
        const unsubPools = poolsQuery.onSnapshot((snapshot) => {
            const map: Record<string, EvaluatorPool> = {};
            snapshot.docs.forEach(doc => {
                const data = doc.data();
                if (!matchesEvalYear(data.evalYear, evalYear, legacyYearFallback)) return;
                const subjectId = data.subjectId || String(doc.id).replace(/^\d{4}_/, '');
                map[subjectId] = { ...data, _docId: doc.id } as EvaluatorPool;
            });
            setEvaluatorPools(map);
        }, dbError("평가자 풀 동기화"));

        return () => {
            unsubAnswers();
            unsubComments();
            unsubSummary();
            unsubPools();
        };
    }, [firebaseUser, sessionUser?.id, sessionUser?.role, evalYear]);

    // 기존 다면평가 원본을 익명 집계로 1회 변환 (HR)
    useEffect(() => {
        if (!sessionUser || sessionUser.role !== 'HR') return;
        let cancelled = false;
        (async () => {
            try {
                const flagRef = rootRef().collection('settings').doc('peer_report_v1');
                const flagSnap = await flagRef.get();
                if (flagSnap.exists || cancelled) return;
                const result = await backfillPeerReportSummaries();
                if (cancelled) return;
                await flagRef.set({
                    done: true,
                    at: Date.now(),
                    contribCount: result.contribCount,
                    summaryCount: result.summaryCount,
                });
                showToast(`기존 다면평가 ${result.summaryCount}건을 익명 리포트로 변환했습니다.`);
            } catch (err) {
                console.error('[PEER] backfill failed:', err);
            }
        })();
        return () => { cancelled = true; };
    }, [sessionUser?.id, sessionUser?.role]);

    // Email Auth + DB 멤버만으로 세션 복원. 익명만으로는 앱 로그인 유지 안 함.
    useEffect(() => {
        if (!membersLoaded || !firebaseUser) return;

        if (firebaseUser.isAnonymous) {
            if (sessionUser || pendingMemberId) {
                clearStoredSession();
                setPendingMemberId(null);
                if (sessionUser) {
                    setSessionUser(null);
                    showToast("보안을 위해 다시 로그인해 주세요.");
                }
            }
            return;
        }

        const memberId = sessionUser?.id || pendingMemberId;
        if (!memberId) return;

        const member = departmentMembers.find(m => m.id === memberId);
        if (!member) {
            clearStoredSession();
            setPendingMemberId(null);
            if (sessionUser) {
                setSessionUser(null);
                showToast("계정 정보를 찾을 수 없어 로그아웃되었습니다. 다시 로그인해 주세요.");
            }
            return;
        }

        if (member.authUid && member.authUid !== firebaseUser.uid) {
            clearStoredSession();
            setPendingMemberId(null);
            setSessionUser(null);
            showToast("계정 인증 정보가 일치하지 않습니다. 다시 로그인해 주세요.");
            return;
        }

        if (isMemberDisabled(member)) {
            clearStoredSession();
            setPendingMemberId(null);
            if (sessionUser) {
                setSessionUser(null);
                showToast("사용이 중지된 계정입니다. HR에 문의하세요.");
            }
            return;
        }

        const next = buildSessionUser(member);
        if (sessionLooksStale(sessionUser, next)) {
            const isFirstBind = !sessionUser;
            setSessionUser(next);
            writeStoredMemberId(next.id);
            setPendingMemberId(null);
            if (isFirstBind) {
                if (next.role === 'HR') {
                    setActiveTab('monitor');
                } else if (next.role === 'EVALUATOR') {
                    setActiveTab('tasks');
                } else {
                    setActiveTab('tasks');
                }
            }
        } else if (pendingMemberId) {
            setPendingMemberId(null);
        }

        if (member.authUid === firebaseUser.uid) {
            writeAuthLink(member).catch((err) => console.error('[AUTH] auth_link refresh failed:', err));
        }
    }, [membersLoaded, departmentMembers, pendingMemberId, sessionUser, firebaseUser]);

    // 가입/등록 폼의 조직-팀 기본값 세팅
    useEffect(() => {
        if (orgTeamList.length === 0) return;
        if (!newMemberOrg) {
            setNewMemberOrg(orgTeamList[0].org);
            setNewMemberTeam(orgTeamList[0].team);
            setNewMemberDept(`${orgTeamList[0].org} ${orgTeamList[0].team}`);
        }
        if (!adminMemberOrg) {
            setAdminMemberOrg(orgTeamList[0].org);
            setAdminMemberTeam(orgTeamList[0].team);
            setAdminMemberDept(`${orgTeamList[0].org} ${orgTeamList[0].team}`);
        }
    }, [orgTeamList]);

    // 다면평가 대상 전환 시 기존 코멘트 로드
    useEffect(() => {
        if (!activeColleagueToEvaluate || !sessionUser) {
            setCommentStrength('');
            setCommentImprovement('');
            return;
        }
        const existing = peerComments.find(c => c.evaluatorId === sessionUser.id && c.colleagueId === activeColleagueToEvaluate.id);
        setCommentStrength(existing?.strength || '');
        setCommentImprovement(existing?.improvement || '');
    }, [activeColleagueToEvaluate, sessionUser, peerComments]);

    // ----------------------------------------------------------------
    // 파생 데이터
    // ----------------------------------------------------------------
    const membersById: Record<string, Member> = {};
    departmentMembers.forEach(m => { membersById[m.id] = m; });

    // 내가 작성한 다면평가 응답: { colleagueId: { questionId: answer } }
    const myGivenAnswers: Record<string, Record<number, string>> = {};
    if (sessionUser) {
        peerAnswers.filter(a => isValidPeerAnswer(a) && a.evaluatorId === sessionUser.id).forEach(a => {
            if (!myGivenAnswers[a.colleagueId]) myGivenAnswers[a.colleagueId] = {};
            myGivenAnswers[a.colleagueId][a.questionId] = a.answer;
        });
    }

    // 내가 평가해야 하는 대상: 확정된 풀에 내가 포함된 subject 목록
    const myEvaluationTargets: Member[] = sessionUser
        ? Object.values(evaluatorPools)
            .filter(p => p.confirmed && Array.isArray(p.selectedIds) && p.selectedIds.includes(sessionUser.id))
            .map(p => membersById[p.subjectId])
            .filter((m): m is Member => Boolean(m))
        : [];

    // 내가 받은 다면평가 집계 — 평가자 ID 없는 익명 리포트만 사용
    const receivedParticipants = peerReportSummary?.participantCount || 0;
    const receivedByCategory = peerReportSummary?.byCategory
        ? {
            Ownership: {
                good: peerReportSummary.byCategory.Ownership?.good || 0,
                bad: peerReportSummary.byCategory.Ownership?.bad || 0,
            },
            'Co-work': {
                good: peerReportSummary.byCategory['Co-work']?.good || 0,
                bad: peerReportSummary.byCategory['Co-work']?.bad || 0,
            },
            Professionalism: {
                good: peerReportSummary.byCategory.Professionalism?.good || 0,
                bad: peerReportSummary.byCategory.Professionalism?.bad || 0,
            },
        }
        : emptyCategoryCounts();
    const receivedStrengths = (peerReportSummary?.strengths || []).map(s => (s || '').trim()).filter(Boolean);
    const receivedImprovements = (peerReportSummary?.improvements || []).map(s => (s || '').trim()).filter(Boolean);

    // 현재 선택된 평가자 풀
    const currentPool: CurrentPool | null = selectedPoolSubject
        ? (evaluatorPools[selectedPoolSubject] || { selectedIds: [] as string[], confirmed: false })
        : null;
    const currentPoolCount = currentPool ? (currentPool.selectedIds || []).length : 0;

    const resolveGrades = (member: Member | null | undefined) => gradesForYear(member, evalYear, legacyYearFallback);

    // Calibration 필터
    const filteredMembers = selectedDeptFilter === 'ALL'
        ? departmentMembers
        : departmentMembers.filter(m => m.department === selectedDeptFilter);
    const membersForCalibration: Member[] = filteredMembers.map(m => {
        const g = resolveGrades(m);
        return { ...m, finalPerf: g.finalPerf, finalBehavior: g.finalBehavior, note: g.note };
    });
    const totalMembers = membersForCalibration.length;
    const exCount = membersForCalibration.filter(m => m.finalPerf === 'EX').length;
    const exPercentage = totalMembers ? Math.round((exCount / totalMembers) * 100) : 0;
    const isLenientWarning = exPercentage > 20;

    // 역할·평가연도별 과제 노출
    const yearTasks = tasks.filter(t => matchesEvalYear(t.evalYear, evalYear, legacyYearFallback));
    const visibleTasks = sessionUser
        ? (sessionUser.role === 'EMPLOYEE'
            ? yearTasks.filter(t => !t.ownerId || t.ownerId === sessionUser.id)
            : yearTasks)
        : [];

    const poolRecommendedIds = selectedPoolSubject
        ? recommendedPoolIds(selectedPoolSubject, yearTasks, departmentMembers)
        : [];
    const poolRecommendedSet = new Set(poolRecommendedIds);

    const changeEvalYear = (year: string | number) => {
        const y = Number(year);
        setEvalYear(y);
        writeStoredEvalYear(y);
        setActiveColleagueToEvaluate(null);
        setSelectedPoolSubject('');
        showToast(`${y}년 평가로 전환했습니다.`);
    };

    const hrMonitor: HrMonitorSnapshot | null = (sessionUser?.role === 'HR')
        ? buildHrMonitorSnapshot({
            evalYear,
            members: departmentMembers,
            tasks: yearTasks,
            pools: evaluatorPools,
            peerReports: peerReportByMember,
            gradesFor: (m) => resolveGrades(m as Member),
        })
        : null;

    // ----------------------------------------------------------------
    // 인증 핸들러
    // ----------------------------------------------------------------
    const denyUnless = (allowedRoles: UserRole[], actionLabel: string) => {
        if (!sessionUser) {
            showToast("로그인이 필요합니다.");
            return false;
        }
        if (!hasRole(sessionUser, allowedRoles)) {
            showToast(`[권한 없음] ${actionLabel}은(는) 허용된 역할만 수행할 수 있습니다.`);
            return false;
        }
        return true;
    };

    const handleLogin = (member: Member) => {
        if (isMemberDisabled(member)) {
            showToast("사용이 중지된 계정입니다. HR에 문의하세요.");
            return;
        }
        const userProfile = buildSessionUser(member);
        setSessionUser(userProfile);
        writeStoredMemberId(member.id);
        setPendingMemberId(null);

        if (userProfile.role === 'EMPLOYEE') {
            setActiveTab('tasks');
        } else if (userProfile.role === 'EVALUATOR') {
            setActiveTab('tasks');
        } else {
            setActiveTab('monitor');
        }
        showToast(`[${userProfile.name}] 님, 사내 인사 포털에 로그인하셨습니다.`);
    };

    const handleRegisterAndLogin = async (e: FormEvent) => {
        e.preventDefault();
        if (isAuthBusy) return;
        if (!publicRegistrationOpen) {
            showToast("현재 공개 가입이 닫혀 있습니다. HR 어드민에서 계정을 생성해 주세요.");
            return;
        }
        if (!newMemberName.trim()) { showToast("이름을 입력해주세요."); return; }
        if (!newMemberDept) { showToast("부서를 선택해주세요."); return; }
        if (!newMemberPassword || newMemberPassword.length < 8) {
            showToast("비밀번호는 8자 이상으로 설정해주세요.");
            return;
        }
        if (newMemberPassword !== newMemberPasswordConfirm) {
            showToast("비밀번호 확인이 일치하지 않습니다.");
            return;
        }
        const isDuplicate = departmentMembers.some(
            m => m.name === newMemberName.trim() && m.department === newMemberDept
        );
        if (isDuplicate) {
            showToast("이미 동일한 이름과 부서로 등록된 구성원이 있습니다. 로그인을 이용해주세요.");
            return;
        }

        setIsAuthBusy(true);
        try {
            const pw = await makePasswordFields(newMemberPassword);
            const newId = `m-${Date.now()}`;
            const email = authEmailFor(newId);

            // 1) Firebase Email Auth 계정 생성 (익명 세션 → 이메일 세션)
            try {
                await auth.createUserWithEmailAndPassword(email, newMemberPassword);
            } catch (authErr) {
                if (isAuthProviderError(authErr)) {
                    showToast("Firebase 콘솔에서 Email/Password 로그인을 활성화해 주세요. (Authentication > Sign-in method)");
                    return;
                }
                throw authErr;
            }

            const uid = auth.currentUser?.uid;
            if (!uid || auth.currentUser?.isAnonymous) {
                showToast("계정 생성에 실패했습니다. 다시 시도해 주세요.");
                return;
            }

            // 2) 멤버 문서 (authUid 포함, 팀원 고정)
            const newProfile: Member = {
                id: newId,
                name: newMemberName.trim(),
                duty: '팀원',
                department: newMemberDept,
                authUid: uid,
                authEmail: email,
                salt: pw.salt,
                passwordHash: pw.passwordHash,
                perfGrade: 'GD', behaviorGrade: 'GD', finalPerf: 'GD', finalBehavior: 'GD',
                note: '공개 가입(팀원)',
                createdAt: Date.now()
            };
            await rootRef().collection('members').doc(newId).set(newProfile);
            await writeAuthLink(newProfile);
            handleLogin(newProfile);
            setNewMemberName('');
            setNewMemberPassword('');
            setNewMemberPasswordConfirm('');
        } catch (err) {
            console.error(err);
            if (isAuthProviderError(err)) {
                showToast("Firebase 콘솔에서 Email/Password 로그인을 활성화해 주세요.");
            } else {
                dbError("임직원 신규 등록")(err);
            }
        } finally {
            setIsAuthBusy(false);
        }
    };

    const handlePasswordLogin = async (e: FormEvent) => {
        e.preventDefault();
        if (isAuthBusy) return;
        setLoginError('');
        if (!loginName.trim() || !loginPassword) {
            setLoginError("이름과 비밀번호를 모두 입력해주세요.");
            return;
        }
        const matched = departmentMembers.filter(m => m.name === loginName.trim());
        if (matched.length === 0) {
            setLoginError("등록되지 않은 이름입니다. 신규 가입을 이용해주세요.");
            return;
        }

        setIsAuthBusy(true);
        try {
            let member: Member | null = null;
            for (const candidate of matched) {
                if (await verifyPassword(candidate, loginPassword)) { member = candidate; break; }
            }
            if (!member) {
                setLoginError("이름 또는 비밀번호가 일치하지 않습니다.");
                return;
            }
            if (isMemberDisabled(member)) {
                setLoginError("사용이 중지된 계정입니다. HR에 문의하세요.");
                return;
            }

            // Firebase Email Auth 세션 + authUid + auth_links 바인딩
            try {
                await bindMemberSession(member, loginPassword);
            } catch (authErr) {
                console.error('[AUTH] bindMemberSession failed:', authErr);
                setLoginError(describeAuthError(authErr));
                return;
            }

            // Auth 세션 확보 후 구버전 해시 업그레이드 (본인 문서만 수정 가능)
            if (!member.salt) {
                const upgraded = await makePasswordFields(loginPassword);
                await rootRef().collection('members').doc(member.id).update(upgraded).catch(() => {});
                member = { ...member, ...upgraded };
            }

            const refreshed: Member = {
                ...member,
                authUid: auth.currentUser?.uid,
                authEmail: member.authEmail || authEmailFor(member.id),
            };
            handleLogin(refreshed);
            writeAuditLog({
                action: 'login',
                actorId: refreshed.id,
                actorName: refreshed.name,
                detail: refreshed.duty,
            });
            setLoginName('');
            setLoginPassword('');
        } catch (err) {
            console.error(err);
            setLoginError("로그인 처리 중 오류가 발생했습니다.");
        } finally {
            setIsAuthBusy(false);
        }
    };

    const handleLogout = async () => {
        setSessionUser(null);
        setPendingMemberId(null);
        setActiveColleagueToEvaluate(null);
        clearStoredSession();
        try {
            await clearAuthLink();
            await auth.signOut();
            // onAuthStateChanged가 익명 재로그인을 처리
        } catch (err) {
            console.error('[AUTH] logout error:', err);
        }
        showToast("포털에서 안전하게 로그아웃되었습니다.");
    };

    const handleChangePassword = async (e: FormEvent) => {
        e.preventDefault();
        setPwError('');
        if (!pwNew || pwNew.length < 8) { setPwError("새 비밀번호는 8자 이상이어야 합니다."); return; }
        if (pwNew !== pwNewConfirm) { setPwError("새 비밀번호 확인이 일치하지 않습니다."); return; }
        const me = sessionUser ? membersById[sessionUser.id] : undefined;
        if (!me) { setPwError("계정 정보를 찾을 수 없습니다. 다시 로그인해주세요."); return; }
        if (!(await verifyPassword(me, pwCurrent))) { setPwError("현재 비밀번호가 일치하지 않습니다."); return; }
        try {
            const pw = await makePasswordFields(pwNew);
            await rootRef().collection('members').doc(me.id).update(pw);
            // Firebase Auth 비밀번호도 동기화 (다음 로그인/규칙 검증용)
            if (auth.currentUser && !auth.currentUser.isAnonymous) {
                await auth.currentUser.updatePassword(pwNew);
            }
            setIsPwModalOpen(false);
            setPwCurrent(''); setPwNew(''); setPwNewConfirm('');
            showToast("비밀번호가 안전하게 변경되었습니다.");
        } catch (err) {
            const code = (err as { code?: string })?.code || '';
            if (code === 'auth/requires-recent-login') {
                setPwError("보안을 위해 다시 로그인하신 뒤 비밀번호를 변경해 주세요.");
                return;
            }
            dbError("비밀번호 변경")(err);
        }
    };

    // ----------------------------------------------------------------
    // 중점 과제 핸들러
    // ----------------------------------------------------------------
    const handleTaskSubmit = (newTaskData: NewTaskPayload) => {
        if (!denyUnless(['EMPLOYEE'], '중점 과제 등록')) return;
        if (seasonLock.taskRegisterLocked && sessionUser?.role !== 'HR') {
            showToast(`${evalYear}년 과제 등록이 시즌 잠금되었습니다. HR에 문의하세요.`);
            return;
        }
        const me = sessionUser!;
        const taskId = `task-${Date.now()}`;
        const emptyQuarter = newTaskData.type === 'QUANTITATIVE'
            ? { rating: 'POOR', score: 80, rate: 0, confirmed: false }
            : { rating: 'POOR', score: 80, checks: { suitability: false, timeliness: false, collaboration: false, effort: false }, confirmed: false };
        const coworkerIds = Array.isArray(newTaskData.coworkerIds) ? newTaskData.coworkerIds : [];
        const taskDoc = {
            id: taskId,
            ...newTaskData,
            evalYear,
            coworkerIds,
            coworkers: formatCoworkerNames(coworkerIds, membersById),
            ownerId: me.id,
            ownerName: me.name,
            ownerDept: me.department,
            status: 'REGISTERED',
            achievementRate: newTaskData.type === 'QUANTITATIVE' ? 0 : null,
            quarterReviews: { Q1: { ...emptyQuarter }, Q2: { ...emptyQuarter }, Q3: { ...emptyQuarter }, Q4: { ...emptyQuarter } },
            createdAt: Date.now()
        };
        rootRef().collection('tasks').doc(taskId).set(taskDoc)
            .then(() => {
                setIsRegisterOpen(false);
                showToast("중점 과제가 등록되었습니다. 리더 승인(Assign Confirm) 후 확정됩니다.");
            })
            .catch(dbError("중점 과제 등록"));
    };

    const assertQuarterEditable = (task: TaskRecord | undefined, quarter: string) => {
        if (!task) return false;
        if (task.status !== 'APPROVED') {
            showToast("승인된 과제만 분기 실적을 입력할 수 있습니다. 먼저 과제 Assign(승인)을 완료해 주세요.");
            return false;
        }
        if (isQuarterSeasonLocked(seasonLock, quarter) && sessionUser?.role !== 'HR') {
            showToast(`${evalYear}년 ${quarter}는 시즌 잠금 상태입니다. HR만 예외 처리할 수 있습니다.`);
            return false;
        }
        const qReview = (task.quarterReviews || {})[quarter] || {};
        if (qReview.confirmed) {
            showToast(`${quarter} 평가는 이미 확정되어 수정할 수 없습니다. 확정 해제 후 다시 시도해 주세요.`);
            return false;
        }
        return true;
    };

    const handleQuantitativeChange = (taskId: string, quarter: string, rateValue: string) => {
        if (!denyUnless(['EVALUATOR'], '분기 실적 입력')) return;
        const currentTask = tasks.find(t => t.id === taskId);
        if (!assertQuarterEditable(currentTask, quarter)) return;
        const rate = Math.max(0, parseFloat(rateValue) || 0);
        const { rating, score } = calculateQuantitativeRating(rate);
        const qReview = (currentTask!.quarterReviews || {})[quarter] || {};
        rootRef().collection('tasks').doc(taskId).update({
            [`quarterReviews.${quarter}`]: {
                ...qReview,
                rating,
                score,
                rate,
                confirmed: false,
            }
        }).catch(dbError("분기 실적 저장"));
    };

    const handleQualitativeCheckToggle = (taskId: string, quarter: string, field: string) => {
        if (!denyUnless(['EVALUATOR'], '정성 평가 입력')) return;
        const currentTask = tasks.find(t => t.id === taskId);
        if (!assertQuarterEditable(currentTask, quarter)) return;
        const qReview = (currentTask!.quarterReviews || {})[quarter] || { checks: {} };
        const updatedChecks = { ...(qReview.checks || {}), [field]: !(qReview.checks || {})[field] };
        const { rating, score } = calculateQualitativeRating(updatedChecks);
        rootRef().collection('tasks').doc(taskId).update({
            [`quarterReviews.${quarter}`]: {
                ...qReview,
                checks: updatedChecks,
                rating,
                score,
                confirmed: false,
            }
        }).catch(dbError("분기 평가 저장"));
    };

    const handleConfirmQuarter = (taskId: string, quarter: string) => {
        if (!denyUnless(['EVALUATOR'], '분기 평가 확정')) return;
        if (isQuarterSeasonLocked(seasonLock, quarter) && sessionUser?.role !== 'HR') {
            showToast(`${evalYear}년 ${quarter}는 시즌 잠금 상태입니다.`);
            return;
        }
        const me = sessionUser!;
        const currentTask = tasks.find(t => t.id === taskId);
        if (!currentTask) return;
        if (currentTask.status !== 'APPROVED') {
            showToast("승인된 과제만 분기 확정이 가능합니다.");
            return;
        }
        const qReview = (currentTask.quarterReviews || {})[quarter] || {};
        rootRef().collection('tasks').doc(taskId).update({
            [`quarterReviews.${quarter}`]: {
                ...qReview,
                confirmed: true,
                confirmedAt: Date.now(),
                confirmedBy: me.id,
            }
        })
            .then(() => {
                writeAuditLog({
                    action: 'quarter.confirm',
                    actorId: me.id,
                    actorName: me.name,
                    targetId: taskId,
                    evalYear,
                    detail: quarter,
                });
                showToast(`${quarter} 평가가 파트장 Confirm으로 확정되었습니다.`);
            })
            .catch(dbError("분기 확정"));
    };

    const handleUnlockQuarter = (taskId: string, quarter: string) => {
        if (!denyUnless(['EVALUATOR'], '분기 확정 해제')) return;
        if (isQuarterSeasonLocked(seasonLock, quarter) && sessionUser?.role !== 'HR') {
            showToast(`${evalYear}년 ${quarter}는 시즌 잠금 상태입니다. 확정 해제할 수 없습니다.`);
            return;
        }
        const currentTask = tasks.find(t => t.id === taskId);
        if (!currentTask) return;
        const qReview = (currentTask.quarterReviews || {})[quarter] || {};
        rootRef().collection('tasks').doc(taskId).update({
            [`quarterReviews.${quarter}`]: {
                ...qReview,
                confirmed: false,
                confirmedAt: null,
                confirmedBy: null,
            }
        })
            .then(() => showToast(`${quarter} 확정이 해제되었습니다.`))
            .catch(dbError("분기 확정 해제"));
    };

    const handleUpdateStatus = (taskId: string, status: string) => {
        if (!denyUnless(['EVALUATOR'], '과제 승인/반려')) return;
        rootRef().collection('tasks').doc(taskId).update({ status })
            .then(() => showToast(`과제 Assign이 [${status === 'APPROVED' ? '승인' : '반려'}]으로 확정되었습니다.`))
            .catch(dbError("과제 상태 변경"));
    };

    // ----------------------------------------------------------------
    // 다면평가 핸들러 (평가자 본인 식별자 포함하도록 데이터 모델 개편)
    // ----------------------------------------------------------------
    const mySubmittedColleagueIds = new Set(
        sessionUser
            ? peerComments
                .filter(c => c.evaluatorId === sessionUser.id && isPeerEvalSubmitted(c.status))
                .map(c => c.colleagueId)
            : []
    );

    const handleAnswerSelect = (colleague: Member, questionId: number, value: string) => {
        if (!sessionUser) { showToast("로그인이 필요합니다."); return; }
        if (seasonLock.peerEvalLocked && sessionUser.role !== 'HR') {
            showToast(`${evalYear}년 다면평가가 시즌 잠금되었습니다.`);
            return;
        }
        if (mySubmittedColleagueIds.has(colleague.id)) {
            showToast("이미 제출 완료된 평가입니다. 수정할 수 없습니다.");
            return;
        }
        const docId = peerEvalDocId(evalYear, sessionUser.id, colleague.id, questionId);
        Promise.all([
            rootRef().collection('peer_evals').doc(docId).set({
                evalYear,
                evaluatorId: sessionUser.id,
                colleagueId: colleague.id,
                colleagueName: colleague.name,
                questionId,
                answer: value,
                updatedAt: Date.now()
            }),
            upsertPeerAnswerToReport(evalYear, sessionUser.id, colleague.id, questionId, value),
        ]).catch((err) => {
            if ((err as { message?: string })?.message === 'ALREADY_SUBMITTED') {
                showToast("이미 제출 완료된 평가입니다. 수정할 수 없습니다.");
                return;
            }
            dbError("다면평가 응답 저장")(err);
        });
    };

    const saveComments = (colleague: Member) => {
        if (!sessionUser) return Promise.reject(new Error('Not logged in'));
        if (seasonLock.peerEvalLocked && sessionUser.role !== 'HR') {
            return Promise.reject(new Error('SEASON_LOCKED'));
        }
        if (mySubmittedColleagueIds.has(colleague.id)) {
            return Promise.reject(new Error('ALREADY_SUBMITTED'));
        }
        const docId = peerCommentDocId(evalYear, sessionUser.id, colleague.id);
        return Promise.all([
            rootRef().collection('peer_comments').doc(docId).set({
                evalYear,
                evaluatorId: sessionUser.id,
                colleagueId: colleague.id,
                colleagueName: colleague.name,
                strength: commentStrength.trim(),
                improvement: commentImprovement.trim(),
                status: 'draft',
                updatedAt: Date.now()
            }, { merge: true }),
            upsertPeerCommentsToReport(
                evalYear,
                sessionUser.id,
                colleague.id,
                commentStrength,
                commentImprovement
            ),
        ]);
    };

    const handleSaveDraft = (colleague: Member) => {
        saveComments(colleague)
            .then(() => {
                setActiveColleagueToEvaluate(null);
                showToast("작성 내용이 임시 저장되었습니다. 언제든 이어서 작성할 수 있습니다.");
            })
            .catch((err) => {
                if ((err as { message?: string })?.message === 'SEASON_LOCKED') {
                    showToast(`${evalYear}년 다면평가가 시즌 잠금되었습니다.`);
                    return;
                }
                if ((err as { message?: string })?.message === 'ALREADY_SUBMITTED') {
                    showToast("이미 제출 완료된 평가입니다. 수정할 수 없습니다.");
                    return;
                }
                dbError("임시 저장")(err);
            });
    };

    const handleCompleteEvaluation = (colleague: Member) => {
        if (!sessionUser) { showToast("로그인이 필요합니다."); return; }
        if (seasonLock.peerEvalLocked && sessionUser.role !== 'HR') {
            showToast(`${evalYear}년 다면평가가 시즌 잠금되었습니다.`);
            return;
        }
        if (mySubmittedColleagueIds.has(colleague.id)) {
            showToast("이미 제출 완료된 평가입니다.");
            return;
        }
        const answered = Object.keys(myGivenAnswers[colleague.id] || {}).length;
        if (answered < surveyQuestions.length) {
            showToast(`아직 응답하지 않은 문항이 ${surveyQuestions.length - answered}개 있습니다. 모든 문항에 답변해주세요.`);
            return;
        }
        submitPeerEvaluation(
            evalYear,
            sessionUser.id,
            colleague.id,
            colleague.name,
            commentStrength,
            commentImprovement
        )
            .then(() => {
                setActiveColleagueToEvaluate(null);
                showToast(`[${colleague.name}] 님에 대한 다면평가가 완료 제출되었습니다. 이후에는 수정할 수 없습니다.`);
            })
            .catch((err) => {
                if ((err as { message?: string })?.message === 'ALREADY_SUBMITTED') {
                    showToast("이미 제출 완료된 평가입니다.");
                    return;
                }
                dbError("다면평가 제출")(err);
            });
    };

    // 평가자 풀 (피평가 대상자별로 개별 관리, DB 영속화)
    const handleTogglePoolMember = (memberId: string) => {
        if (!denyUnless(['EVALUATOR', 'HR'], '평가자 풀 구성')) return;
        if (!selectedPoolSubject || !currentPool) return;
        if (currentPool.confirmed) {
            showToast("이미 확정된 풀입니다. 수정하려면 먼저 [확정 해제]를 눌러주세요.");
            return;
        }
        const ids = currentPool.selectedIds || [];
        const next = ids.includes(memberId) ? ids.filter(i => i !== memberId) : [...ids, memberId];
        const subject = membersById[selectedPoolSubject];
        const docId = poolDocId(evalYear, selectedPoolSubject);
        rootRef().collection('evaluator_pools').doc(docId).set({
            evalYear,
            subjectId: selectedPoolSubject,
            subjectName: subject ? subject.name : '',
            selectedIds: next,
            confirmed: false,
            updatedAt: Date.now()
        }, { merge: true }).catch(dbError("평가자 풀 변경"));
    };

    const handleApplyRecommendedPool = () => {
        if (!denyUnless(['EVALUATOR', 'HR'], '평가자 풀 추천 적용')) return;
        if (!selectedPoolSubject || !currentPool) return;
        if (currentPool.confirmed) {
            showToast("이미 확정된 풀입니다. 수정하려면 먼저 [확정 해제]를 눌러주세요.");
            return;
        }
        const recommended = recommendedPoolIds(selectedPoolSubject, yearTasks, departmentMembers);
        if (recommended.length === 0) {
            showToast("추천할 협업 동료/동일 부서 인원이 없습니다. 과제에 Co-worker를 지정해 주세요.");
            return;
        }
        const subject = membersById[selectedPoolSubject];
        rootRef().collection('evaluator_pools').doc(poolDocId(evalYear, selectedPoolSubject)).set({
            evalYear,
            subjectId: selectedPoolSubject,
            subjectName: subject ? subject.name : '',
            selectedIds: recommended,
            confirmed: false,
            updatedAt: Date.now()
        }, { merge: true })
            .then(() => showToast(`Co-worker·동일부서 기준 ${recommended.length}명을 풀에 반영했습니다. 검토 후 확정해 주세요.`))
            .catch(dbError("평가자 풀 추천 적용"));
    };

    const handleConfirmEvaluatorPool = () => {
        if (!denyUnless(['EVALUATOR', 'HR'], '평가자 풀 확정')) return;
        if (!selectedPoolSubject || !currentPool) return;
        if (currentPoolCount < 3 || currentPoolCount > 8) {
            showToast('다면 평가자 수는 최소 3명, 최대 8명까지 지정 가능합니다. (신뢰도 권장: 6~8명)');
            return;
        }
        rootRef().collection('evaluator_pools').doc(poolDocId(evalYear, selectedPoolSubject)).set({
            evalYear,
            subjectId: selectedPoolSubject,
            confirmed: true,
            updatedAt: Date.now()
        }, { merge: true })
            .then(() => showToast('다면 평가 매칭 풀이 최종 확정되었습니다. 지정된 동료들에게 설문이 노출됩니다.'))
            .catch(dbError("평가자 풀 확정"));
    };

    const handleUnlockEvaluatorPool = () => {
        if (!denyUnless(['EVALUATOR', 'HR'], '평가자 풀 확정 해제')) return;
        if (!selectedPoolSubject) return;
        rootRef().collection('evaluator_pools').doc(poolDocId(evalYear, selectedPoolSubject)).set({
            evalYear,
            subjectId: selectedPoolSubject,
            confirmed: false,
            updatedAt: Date.now()
        }, { merge: true })
            .then(() => showToast('평가자 풀 확정이 해제되었습니다. 구성원을 다시 조정할 수 있습니다.'))
            .catch(dbError("평가자 풀 확정 해제"));
    };

    // ----------------------------------------------------------------
    // Calibration 핸들러
    // ----------------------------------------------------------------
    const handleSaveCalibration = () => {
        if (!denyUnless(['EVALUATOR', 'HR'], 'Calibration 등급 확정')) return;
        if (seasonLock.calibrationLocked && sessionUser?.role !== 'HR') {
            showToast(`${evalYear}년 Calibration이 시즌 잠금되었습니다.`);
            return;
        }
        if (!selectedMemberForCalibration) return;
        const targetName = selectedMemberForCalibration.name;
        const yearKey = String(evalYear);
        const peerSuggest = suggestBehaviorFromPeer(peerReportByMember[selectedMemberForCalibration.id] as never);
        const payload: Record<string, unknown> = {
            [`yearlyGrades.${yearKey}.finalPerf`]: editPerfGrade,
            [`yearlyGrades.${yearKey}.finalBehavior`]: editBehaviorGrade,
            [`yearlyGrades.${yearKey}.note`]: editNote,
            [`yearlyGrades.${yearKey}.updatedAt`]: Date.now(),
            gradeYear: evalYear,
            finalPerf: editPerfGrade,
            finalBehavior: editBehaviorGrade,
            note: editNote,
        };
        if (peerSuggest) {
            payload[`yearlyGrades.${yearKey}.peerSuggest`] = {
                grade: peerSuggest.grade,
                positivityRate: peerSuggest.positivityRate,
                participantCount: peerSuggest.participantCount,
                appliedBehavior: editBehaviorGrade === peerSuggest.grade,
                at: Date.now(),
            };
        }
        rootRef().collection('members').doc(selectedMemberForCalibration.id).update(payload).then(() => {
            writeAuditLog({
                action: 'calibration.save',
                actorId: sessionUser?.id,
                actorName: sessionUser?.name,
                targetId: selectedMemberForCalibration.id,
                targetName: targetName,
                evalYear,
                detail: `perf=${editPerfGrade} behavior=${editBehaviorGrade}`,
            });
            setSelectedMemberForCalibration(null);
            showToast(`[${targetName}] ${evalYear}년 9-Block 등급이 반영되었습니다.`);
        }).catch(dbError("Calibration 저장"));
    };

    const openCalibrationModal = (member: Member) => {
        const g = resolveGrades(member);
        setSelectedMemberForCalibration(member);
        setEditPerfGrade(g.finalPerf);
        setEditBehaviorGrade(g.finalBehavior);
        setEditNote(g.note);
    };

    const applyPeerBehaviorSuggest = () => {
        if (!selectedMemberForCalibration) return;
        const peerSuggest = suggestBehaviorFromPeer(peerReportByMember[selectedMemberForCalibration.id] as never);
        if (!peerSuggest) {
            showToast("적용할 다면평가 집계가 없습니다.");
            return;
        }
        setEditBehaviorGrade(peerSuggest.grade);
        const stamp = `[다면→방식 추천] ${peerSuggest.reason} → ${peerSuggest.grade}`;
        setEditNote((prev) => {
            const base = (prev || '').trim();
            if (base.includes('[다면→방식 추천]')) return base;
            return base ? `${base}\n${stamp}` : stamp;
        });
        showToast(`일하는 방식 등급에 다면평가 추천(${peerSuggest.grade})을 반영했습니다. 저장을 눌러 확정하세요.`);
    };

    // ----------------------------------------------------------------
    // HR 어드민 핸들러
    // ----------------------------------------------------------------
    const handleSaveSeasonLock = (next: YearSeasonLock) => {
        if (!denyUnless(['HR'], '시즌 잠금 설정')) return;
        const payload: YearSeasonLock = {
            ...normalizeSeasonLock(evalYear, next),
            updatedAt: Date.now(),
            updatedBy: sessionUser?.id,
        };
        rootRef().collection('settings').doc(seasonLockDocId(evalYear)).set(payload, { merge: true })
            .then(() => {
                writeAuditLog({
                    action: 'season_lock.update',
                    actorId: sessionUser?.id,
                    actorName: sessionUser?.name,
                    evalYear,
                    detail: `Q=${JSON.stringify(payload.quarters)} peer=${payload.peerEvalLocked} task=${payload.taskRegisterLocked} cal=${payload.calibrationLocked}`,
                });
                showToast(`${evalYear}년 시즌 잠금 설정을 저장했습니다.`);
            })
            .catch(dbError("시즌 잠금 저장"));
    };

    const handleSetPublicRegistration = (open: boolean) => {
        if (!denyUnless(['HR'], '공개 가입 설정')) return;
        rootRef().collection('settings').doc('registration').set({
            publicOpen: open,
            updatedAt: Date.now(),
            updatedBy: sessionUser?.id,
        }, { merge: true })
            .then(() => {
                writeAuditLog({
                    action: 'settings.registration',
                    actorId: sessionUser?.id,
                    actorName: sessionUser?.name,
                    detail: open ? 'public_open' : 'public_closed',
                });
                showToast(open ? '공개 가입을 열었습니다.' : '공개 가입을 닫았습니다. HR만 계정 생성 가능합니다.');
            })
            .catch(dbError("가입 설정 저장"));
    };

    const handleAddDepartment = (e: FormEvent) => {
        e.preventDefault();
        if (!denyUnless(['HR'], '부서 등록')) return;
        const org = newDeptOrg.trim();
        const team = newDeptTeam.trim();
        if (!org || !team) { showToast("상위 조직과 하위 팀명을 모두 입력해주세요."); return; }
        const fullName = `${org} ${team}`;
        if (departments.includes(fullName)) { showToast('이미 등록되어 있는 부서명입니다.'); return; }
        rootRef().collection('departments').doc(fullName).set({ name: fullName, org, team })
            .then(() => {
                setNewDeptOrg(''); setNewDeptTeam('');
                showToast(`신규 부서 [${fullName}] 가 추가되었습니다.`);
            })
            .catch(dbError("부서 등록"));
    };

    const handleAddMember = async (e: FormEvent) => {
        e.preventDefault();
        if (!denyUnless(['HR'], '임직원 등록')) return;
        if (!newMemberName.trim()) return;
        if (!adminMemberDept) { showToast("소속 조직/팀을 선택해주세요."); return; }
        if (!adminInitPassword || adminInitPassword.length < 8) {
            showToast("초기 비밀번호를 8자 이상으로 입력해주세요.");
            return;
        }
        const isDuplicate = departmentMembers.some(
            m => m.name === newMemberName.trim() && m.department === adminMemberDept
        );
        if (isDuplicate) { showToast("동일한 이름과 부서로 이미 등록된 구성원이 있습니다."); return; }

        try {
            const pw = await makePasswordFields(adminInitPassword);
            const newId = `m-${Date.now()}`;
            let authUid = '';
            try {
                authUid = await createAuthUserKeepingSession(newId, adminInitPassword);
            } catch (authErr) {
                if (isAuthProviderError(authErr)) {
                    showToast("Firebase 콘솔에서 Email/Password 로그인을 활성화해 주세요.");
                    return;
                }
                throw authErr;
            }
            const email = authEmailFor(newId);
            const newMember: Member = {
                id: newId,
                name: newMemberName.trim(),
                duty: newMemberDuty,
                department: adminMemberDept,
                isAdmin: newMemberDuty === '담당자',
                evalGroup: newMemberEvalGroup || '',
                authUid,
                authEmail: email,
                salt: pw.salt,
                passwordHash: pw.passwordHash,
                perfGrade: newMemberPerf,
                behaviorGrade: newMemberBehav,
                finalPerf: newMemberPerf,
                finalBehavior: newMemberBehav,
                note: newMemberNote.trim(),
                createdAt: Date.now()
            };
            await rootRef().collection('members').doc(newId).set(newMember);
            setNewMemberName(''); setNewMemberNote(''); setAdminInitPassword(''); setNewMemberEvalGroup('');
            showToast(`신규 임직원 [${newMember.name}] 등록 완료. 설정한 초기 비밀번호를 본인에게 안내하고, 첫 로그인 후 변경하도록 해주세요.`);
        } catch (err) {
            dbError("임직원 등록")(err);
        }
    };

    const handleDeleteMember = async (memberId: string, name: string) => {
        if (!denyUnless(['HR'], '임직원 삭제')) return;
        if (sessionUser && memberId === sessionUser.id) {
            showToast("현재 로그인 중인 본인 계정은 삭제할 수 없습니다.");
            return;
        }
        if (!window.confirm(`[${name}] 님의 사원 정보를 영구 삭제합니다.\n과제·다면평가·평가자 풀 등 관련 데이터도 함께 정리되며 되돌릴 수 없습니다. 계속하시겠습니까?`)) return;
        try {
            const result = await deleteMemberCascade(memberId);
            writeAuditLog({
                action: 'member.delete',
                actorId: sessionUser?.id,
                actorName: sessionUser?.name,
                targetId: memberId,
                targetName: name,
                detail: `deletedDocs=${result.deletedDocs}`,
            });
            showToast(`[${name}] 삭제 완료 (관련 문서 ${result.deletedDocs}건 정리).`);
        } catch (err) {
            dbError("임직원 삭제")(err);
        }
    };

    const handleSetMemberDisabled = async (memberId: string, name: string, disabled: boolean) => {
        if (!denyUnless(['HR'], '계정 사용 중지/복구')) return;
        if (sessionUser && memberId === sessionUser.id) {
            showToast("현재 로그인 중인 본인 계정은 중지할 수 없습니다.");
            return;
        }
        const action = disabled ? '사용 중지' : '사용 복구';
        if (!window.confirm(`[${name}] 님 계정을 ${action}하시겠습니까?`)) return;
        try {
            await setMemberDisabled(memberId, disabled, sessionUser?.id);
            writeAuditLog({
                action: disabled ? 'member.disable' : 'member.enable',
                actorId: sessionUser?.id,
                actorName: sessionUser?.name,
                targetId: memberId,
                targetName: name,
            });
            showToast(
                disabled
                    ? `[${name}] 계정 사용 중지 완료. (Firebase Auth 완전 비활성은 콘솔/Admin SDK 필요)`
                    : `[${name}] 계정 사용 복구 완료.`
            );
        } catch (err) {
            dbError(action)(err);
        }
    };

    const handleExportToExcel = () => {
        if (!denyUnless(['HR'], '엑셀 내보내기')) return;
        try {
            const rawMemberData = departmentMembers.map(m => ({
                '사원 고유 코드': m.id,
                '임직원명': m.name,
                '직책/직급': m.duty,
                '소속 부서': m.department || '미지정',
                '평가군 (Category)': evalGroupLabel(m.evalGroup),
                '1차 성과 평가': m.perfGrade,
                '1차 일하는 방식': m.behaviorGrade,
                '최종 확정 성과 (Calibration)': m.finalPerf,
                '최종 확정 방식 (Calibration)': m.finalBehavior,
                '9-Block 유형 판정': getBlockTitle(m.finalPerf || 'GD', m.finalBehavior || 'GD'),
                '등급 조정 기록 (Note)': m.note || '특이사항 없음'
            }));

            const allDepts = [...new Set([...departments, ...departmentMembers.map(m => m.department).filter(Boolean)])];
            const summaryData = allDepts.map(dept => {
                const deptMembers = departmentMembers.filter(m => m.department === dept);
                const total = deptMembers.length;
                const ex = deptMembers.filter(m => m.finalPerf === 'EX').length;
                const gd = deptMembers.filter(m => m.finalPerf === 'GD').length;
                const ni = deptMembers.filter(m => m.finalPerf === 'NI').length;
                const exRatio = total ? Math.round((ex / total) * 100) : 0;
                return {
                    '부서명': dept,
                    '전체 소속원 수': total,
                    'EX 등급 수': ex,
                    'GD 등급 수': gd,
                    'NI 등급 수': ni,
                    'EX 등급 비중 (%)': `${exRatio}% (기준 20%)`
                };
            });

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rawMemberData), "임직원 평가 관리 마스터");
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryData), "부서별 등급 요약 통계");

            const today = new Date().toISOString().slice(0, 10);
            XLSX.writeFile(wb, `Personnel_Evaluation_Report_${today}.xlsx`);
            showToast("최종 평가 보고서가 다중 시트 엑셀 문서로 다운로드되었습니다.");
        } catch (error) {
            console.error("Excel Export Error: ", error);
            showToast("엑셀 파일 빌드 처리 중 오류가 발생했습니다.");
        }
    };

    const handleRunDbAudit = async () => {
        if (!denyUnless(['HR'], 'DB 무결성 점검')) return;
        try {
            const result = await runPeerEvalAudit();
            const report = formatAuditReport(result);
            console.log(report);
            console.log('[AUDIT] Full result:', result);
            const legacyCount =
                result.evals.legacy_missing_evaluator +
                result.evals.legacy_wrong_doc_id +
                result.comments.legacy_missing_evaluator +
                result.comments.legacy_wrong_doc_id;
            if (legacyCount > 0) {
                showToast(`DB 점검: 구버전 다면평가 데이터 ${legacyCount}건 발견. 콘솔(F12)에서 상세 확인하세요.`);
            } else {
                showToast(`DB 점검 완료: 활성 다면평가 데이터 정상 (evals ${result.evals.valid}/${result.evals.total})`);
            }
        } catch (err) {
            console.error('[AUDIT] Error:', err);
            showToast('DB 점검 중 오류가 발생했습니다. Firebase 연결 상태를 확인해주세요.');
        }
    };

    const openPwModal = () => {
        setIsPwModalOpen(true);
        setPwError('');
    };

    const handleSelectTab = (tab: ActiveTab) => {
        setActiveTab(tab);
        if (tab === 'calibration') setSelectedDeptFilter('ALL');
    };

    return {
        // auth / session
        firebaseUser,
        sessionUser,
        evalYear,
        evalYearOptions,
        changeEvalYear,
        // realtime data
        tasks,
        departmentMembers,
        departments,
        orgTeamList,
        evaluatorPools,
        seasonLock,
        publicRegistrationOpen,
        peerReportByMember,
        // tabs / modals
        activeTab,
        setActiveTab: handleSelectTab,
        isRegisterOpen,
        setIsRegisterOpen,
        isPwModalOpen,
        setIsPwModalOpen,
        openPwModal,
        // derived
        membersById,
        myGivenAnswers,
        myEvaluationTargets,
        mySubmittedColleagueIds,
        receivedParticipants,
        receivedByCategory,
        receivedStrengths,
        receivedImprovements,
        currentPool,
        currentPoolCount,
        membersForCalibration,
        totalMembers,
        exCount,
        exPercentage,
        isLenientWarning,
        visibleTasks,
        poolRecommendedIds,
        poolRecommendedSet,
        hrMonitor,
        // peer eval interaction
        activeColleagueToEvaluate,
        setActiveColleagueToEvaluate,
        commentStrength,
        setCommentStrength,
        commentImprovement,
        setCommentImprovement,
        selectedPoolSubject,
        setSelectedPoolSubject,
        // calibration modal
        selectedMemberForCalibration,
        setSelectedMemberForCalibration,
        editPerfGrade,
        setEditPerfGrade,
        editBehaviorGrade,
        setEditBehaviorGrade,
        editNote,
        setEditNote,
        selectedDeptFilter,
        setSelectedDeptFilter,
        // admin forms
        newDeptOrg,
        setNewDeptOrg,
        newDeptTeam,
        setNewDeptTeam,
        newMemberName,
        setNewMemberName,
        newMemberDuty,
        setNewMemberDuty,
        newMemberDept,
        setNewMemberDept,
        newMemberOrg,
        setNewMemberOrg,
        newMemberTeam,
        setNewMemberTeam,
        adminMemberOrg,
        setAdminMemberOrg,
        adminMemberTeam,
        setAdminMemberTeam,
        adminMemberDept,
        setAdminMemberDept,
        newMemberPerf,
        setNewMemberPerf,
        newMemberBehav,
        setNewMemberBehav,
        newMemberNote,
        setNewMemberNote,
        newMemberEvalGroup,
        setNewMemberEvalGroup,
        adminInitPassword,
        setAdminInitPassword,
        // login / register forms
        newMemberPassword,
        setNewMemberPassword,
        newMemberPasswordConfirm,
        setNewMemberPasswordConfirm,
        loginName,
        setLoginName,
        loginPassword,
        setLoginPassword,
        loginError,
        isAuthBusy,
        // password change modal
        pwCurrent,
        setPwCurrent,
        pwNew,
        setPwNew,
        pwNewConfirm,
        setPwNewConfirm,
        pwError,
        // toast
        toastMessage,
        // handlers
        handleRegisterAndLogin,
        handlePasswordLogin,
        handleLogout,
        handleChangePassword,
        handleTaskSubmit,
        handleQuantitativeChange,
        handleQualitativeCheckToggle,
        handleConfirmQuarter,
        handleUnlockQuarter,
        handleUpdateStatus,
        handleAnswerSelect,
        handleSaveDraft,
        handleCompleteEvaluation,
        handleTogglePoolMember,
        handleApplyRecommendedPool,
        handleConfirmEvaluatorPool,
        handleUnlockEvaluatorPool,
        handleSaveCalibration,
        openCalibrationModal,
        applyPeerBehaviorSuggest,
        handleAddDepartment,
        handleAddMember,
        handleDeleteMember,
        handleSetMemberDisabled,
        handleExportToExcel,
        handleRunDbAudit,
        handleSaveSeasonLock,
        handleSetPublicRegistration,
    };
}

export type AppStore = ReturnType<typeof useAppStore>;
